import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { authFetch } from '../lib/authFetch';

const MeetingLockContext = createContext({
  active: false,
  meeting: null,
  refresh: () => {},
});

/**
 * MeetingLockProvider keeps the app in sync with the user's "in-progress
 * meeting" state.  When `active === true`, every camera / microphone /
 * speaker control across the app should show as locked (greyed out with
 * a tooltip) until the After selfie is taken.
 *
 * The provider polls /api/meetings/active every 60 s, refreshes
 * immediately on focus, and exposes a `refresh()` for the Meeting Log
 * page to call after check-in / check-out.
 */
export function MeetingLockProvider({ children }) {
  const [meeting, setMeeting] = useState(null);
  const lastFetchRef = useRef(0);

  const refresh = useCallback(async () => {
    if (Date.now() - lastFetchRef.current < 1500) return;
    lastFetchRef.current = Date.now();
    try {
      const r = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/meetings/active`);
      if (r.ok) {
        const data = await r.json();
        setMeeting(data.meeting || null);
      }
    } catch {}
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 60000);
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(id); window.removeEventListener('focus', onFocus); };
  }, [refresh]);

  return (
    <MeetingLockContext.Provider value={{ active: !!meeting, meeting, refresh }}>
      {children}
    </MeetingLockContext.Provider>
  );
}

export function useMeetingLock() {
  return useContext(MeetingLockContext);
}

/**
 * Wraps a child element (typically a button) and disables it whenever a
 * meeting is in progress.  When clicked while locked, shows a toast
 * explaining why.  Use for camera / microphone / speaker controls.
 *
 * Usage:
 *   <MeetingLockGate>
 *     <button onClick={openCamera}>Take photo</button>
 *   </MeetingLockGate>
 */
import { toast } from 'sonner';
export function MeetingLockGate({ children, label = 'This control', disabled = false }) {
  const { active } = useMeetingLock();
  if (!active) return children;
  // Clone child to inject disabled + intercept clicks.
  const child = React.Children.only(children);
  const onClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toast.warning(`${label} is locked while a meeting is in progress.  Take your After selfie in the Meeting Log to release the lock.`);
  };
  return React.cloneElement(child, {
    disabled: true,
    onClick,
    'aria-disabled': true,
    title: `${label} is locked during meetings`,
  });
}
